<script lang="ts">
  /** Inline editor for the modulations driving one target. */
  import type { AppController } from '../../app/controller.svelte';
  import { CURVES, MOD_SOURCES, sourceLabel, type Curve } from '../../shared/modulation';
  import { uid } from '../../shared/scene';
  import Icon from './Icon.svelte';

  interface Props {
    ctl: AppController;
    target: string;
    label: string;
  }
  let { ctl, target, label }: Props = $props();
  const mods = $derived(
    ctl.scene.modulations.filter((m) => m.target === target && m.source !== 'osc'),
  );
  const groups = ['Audio', 'Beat', 'LFO', 'Mood', 'Other'] as const;
  const midiSources = $derived([
    ...new Set([
      ...(ctl.midiVersion >= 0 ? ctl.midi.seenCC : []),
      ...ctl.scene.modulations.filter((m) => m.source.startsWith('midi.')).map((m) => m.source),
    ]),
  ]);

  function add() {
    ctl.dispatch({
      type: 'mod.add',
      modulation: {
        id: uid('m'),
        target,
        source: 'audio.bass',
        amount: 0.3,
        curve: 'linear',
        attackMs: 10,
        releaseMs: 200,
      },
    });
  }
</script>

<div class="mods" data-testid="mod-editor">
  {#each mods as m (m.id)}
    <div class="mod">
      <div class="row">
        <select
          class="grow"
          value={m.source}
          aria-label={`Source for ${label}`}
          onchange={(e) =>
            ctl.dispatch({
              type: 'mod.update',
              id: m.id,
              patch: { source: e.currentTarget.value },
            })}
        >
          {#each groups as g (g)}
            <optgroup label={g}>
              {#each MOD_SOURCES.filter((s) => s.group === g && s.id !== 'osc') as s (s.id)}
                <option value={s.id}>{s.label}</option>
              {/each}
            </optgroup>
          {/each}
          {#if midiSources.length}
            <optgroup label="MIDI">
              {#each midiSources as s (s)}
                <option value={s}>{sourceLabel(s)}</option>
              {/each}
            </optgroup>
          {/if}
        </select>
        <select
          value={m.curve}
          aria-label="Curve"
          onchange={(e) =>
            ctl.dispatch({
              type: 'mod.update',
              id: m.id,
              patch: { curve: e.currentTarget.value as Curve },
            })}
        >
          {#each CURVES as c (c)}
            <option value={c}>{c}</option>
          {/each}
        </select>
        <button
          class="icon-btn"
          aria-label="Remove modulation"
          onclick={() => ctl.dispatch({ type: 'mod.remove', id: m.id })}
          ><Icon name="close" /></button
        >
      </div>
      <label class="mini">
        <span>Amount</span>
        <input
          type="range"
          min="-1"
          max="1"
          step="0.01"
          value={m.amount}
          oninput={(e) =>
            ctl.dispatch(
              { type: 'mod.update', id: m.id, patch: { amount: Number(e.currentTarget.value) } },
              `mod:${m.id}:amount`,
            )}
        />
        <span class="v">{m.amount.toFixed(2)}</span>
      </label>
      <label class="mini">
        <span>Attack</span>
        <input
          type="range"
          min="0"
          max="1000"
          step="5"
          value={m.attackMs}
          oninput={(e) =>
            ctl.dispatch(
              { type: 'mod.update', id: m.id, patch: { attackMs: Number(e.currentTarget.value) } },
              `mod:${m.id}:atk`,
            )}
        />
        <span class="v">{m.attackMs}ms</span>
      </label>
      <label class="mini">
        <span>Release</span>
        <input
          type="range"
          min="0"
          max="3000"
          step="10"
          value={m.releaseMs}
          oninput={(e) =>
            ctl.dispatch(
              { type: 'mod.update', id: m.id, patch: { releaseMs: Number(e.currentTarget.value) } },
              `mod:${m.id}:rel`,
            )}
        />
        <span class="v">{m.releaseMs}ms</span>
      </label>
    </div>
  {/each}
  <button class="link" data-testid="mod-add" onclick={add}>+ Add modulation</button>
  {#if mods.length === 0}
    <p class="hint">Drive “{label}” with the music: bass, the beat, LFOs, mood, or a MIDI knob.</p>
  {/if}
</div>

<style>
  .mods {
    margin: 2px 0 8px 12px;
    padding: 8px 0 8px 10px;
    border-left: 2px solid color-mix(in srgb, var(--accent) 50%, transparent);
    display: grid;
    gap: 8px;
  }
  .mod {
    display: grid;
    gap: 2px;
  }
  select {
    font-size: 12px;
    padding: 3px 6px;
  }
  .mini {
    display: grid;
    grid-template-columns: 56px 1fr 52px;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    color: var(--faint);
  }
  .mini input {
    width: 100%;
    accent-color: var(--accent-2);
  }
  .v {
    font-family: var(--mono);
    font-size: 10.5px;
    text-align: right;
  }
</style>
